import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DeviceService } from './tv-detect.service';
import {
  TvPurchaseRedirectModalComponent,
  TvPurchaseRedirectModalData
} from '../_component/sharedComponents/tv-purchase-redirect-modal/tv-purchase-redirect-modal.component';

/**
 * Centralizes TV-only redirect-modal logic for Subscribe/Rent flows.
 *
 * On TV (DeviceService.isTV()), opens a redirect modal directing users to
 * complete purchase on web/phone. On non-TV, returns false so callers fall
 * through to their existing purchase flow.
 *
 * Usage in a click handler that previously did `if (this.device.isTV()) return;`:
 *
 *     showPlansModal(item: any) {
 *       if (this.tvPurchaseRedirect.maybeShowSubscribe(item?.title)) return;
 *       // ... existing non-TV subscribe flow
 *     }
 */
@Injectable({ providedIn: 'root' })
export class TvPurchaseRedirectService {
  constructor(
    private dialog: MatDialog,
    private device: DeviceService
  ) {}

  /**
   * If running on TV, opens the Subscribe redirect modal and returns true.
   * Otherwise returns false (caller should proceed with normal subscribe flow).
   */
  maybeShowSubscribe(title?: string): boolean {
    if (!this.device.isTV()) return false;
    this.openModal('subscribe', title);
    return true;
  }

  /**
   * If running on TV, opens the Rent redirect modal and returns true.
   * Otherwise returns false (caller should proceed with normal rent flow).
   */
  maybeShowRent(title?: string): boolean {
    if (!this.device.isTV()) return false;
    this.openModal('rent', title);
    return true;
  }

  private openModal(action: 'subscribe' | 'rent', title?: string): void {
    const data: TvPurchaseRedirectModalData = { action, title };
    this.dialog.open(TvPurchaseRedirectModalComponent, {
      data,
      panelClass: 'tv-purchase-redirect-panel',
      disableClose: false,
      autoFocus: true,
      restoreFocus: true,
      width: '640px',
      maxWidth: '90vw'
    });
  }
}
